import { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const SESSION_TOAST_KEY = 'erpz.incompleteDeliveries.toastShown';
const POLL_INTERVAL_MS = 60 * 1000; // refresh setiap 60 detik

/**
 * Hook global untuk fetch jumlah delivery batch yang belum lengkap info pengirimannya
 * dan sudah tercatat > `olderThanHours` (default 24 jam).
 *
 * Return: { count, txCount, transactions, loading, refetch }
 *
 * Otomatis emit toast satu kali per session kalau ada batch pending.
 */
export function useIncompleteDeliveries(olderThanHours = 24, { silent = false } = {}) {
  const [state, setState] = useState({
    count: 0,
    txCount: 0,
    transactions: [],
    loading: true,
  });
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/transactions/incomplete-deliveries`, {
        params: { older_than_hours: olderThanHours },
      });
      const count = Number(data?.total_incomplete_batches || 0);
      const txCount = Number(data?.transaction_count || 0);
      setState({ count, txCount, transactions: data?.transactions || [], loading: false });
      // Toast satu kali per sesi kalau ada pending & belum di-notif
      if (!silent && count > 0) {
        try {
          if (!sessionStorage.getItem(SESSION_TOAST_KEY)) {
            sessionStorage.setItem(SESSION_TOAST_KEY, '1');
            toast.warning(
              `${count} batch pengiriman belum lengkap info sopirnya (>${olderThanHours}j)`,
              {
                description: `${txCount} transaksi perlu perhatian di Cetak Dokumen`,
                duration: 8000,
              },
            );
          }
        } catch (storageErr) {
          // sessionStorage bisa di-block di mode privat/browser tertentu.
          console.debug('[useIncompleteDeliveries] sessionStorage tidak tersedia:', storageErr);
        }
      }
    } catch (e) {
      console.warn('[useIncompleteDeliveries] gagal fetch:', e?.message || e);
      setState((s) => ({ ...s, loading: false }));
    }
  }, [olderThanHours, silent]);

  useEffect(() => {
    fetchData();
    // Poll periodik
    timerRef.current = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchData]);

  return { ...state, refetch: fetchData };
}

/** Clear session flag — panggil setelah user "acknowledge" (opsional). */
export function acknowledgeIncompleteToast() {
  try { sessionStorage.removeItem(SESSION_TOAST_KEY); }
  catch (e) { console.debug('[useIncompleteDeliveries] sessionStorage removeItem gagal:', e); }
}
